import appConfig from '../config/appConfig';
import { Product, Review, Cart, Order, DashboardData, AdminOrder, DiscountCode, DiscountValidation } from '../types/types';
import { mockProducts } from './productsData';

type TokenProvider = () => Promise<string | null>;

class DataService {
    private baseUrl: string;
    private useMockData: boolean;
    private tokenProvider: TokenProvider | null = null;

    constructor() {
        this.baseUrl = appConfig.apiUrl;
        this.useMockData = appConfig.useMockData;
    }

    setTokenProvider(provider: TokenProvider) {
        this.tokenProvider = provider;
    }

    private async authHeaders(): Promise<Record<string, string>> {
        if (!this.tokenProvider) return {};
        const token = await this.tokenProvider();
        if (!token) return {};
        return { Authorization: `Bearer ${token}` };
    }

    async getProducts(): Promise<Product[]> {
        if (this.useMockData) {
            return mockProducts;
        }
        const response = await fetch(`${this.baseUrl}/products`);
        if (!response.ok) throw new Error('Failed to fetch products');
        return response.json();
    }

    async getProduct(id: number): Promise<Product | undefined> {
        if (this.useMockData) {
            return mockProducts.find(p => p.id === id);
        }
        const response = await fetch(`${this.baseUrl}/products/${id}`);
        if (!response.ok) throw new Error('Failed to fetch product');
        return response.json();
    }

    async getReviews(productId: number): Promise<Review[]> {
        if (this.useMockData) {
            const product = mockProducts.find(p => p.id === productId);
            return product?.reviews ?? [];
        }
        const response = await fetch(`${this.baseUrl}/reviews/by-product/${productId}`);
        if (!response.ok) throw new Error('Failed to fetch reviews');
        return response.json();
    }

    async createProduct(product: Partial<Product>): Promise<Product> {
        if (this.useMockData) {
            const newProduct: Product = {
                id: mockProducts.length + 1,
                name: product.name ?? '',
                description: product.description ?? '',
                category: product.category ?? 'Food',
                price: product.price ?? 0,
                imageUrl: product.imageUrl ?? '',
                petType: product.petType ?? 'All',
                inStock: true,
                featured: false,
                rating: 0,
                tags: product.tags ?? [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                reviews: [],
            };
            mockProducts.push(newProduct);
            return newProduct;
        }
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/products`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify(product),
        });
        if (!response.ok) throw new Error('Failed to create product');
        return response.json();
    }

    async updateProduct(id: number, product: Partial<Product>): Promise<Product> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/products/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify(product),
        });
        if (!response.ok) throw new Error('Failed to update product');
        return response.json();
    }

    async deleteProduct(id: number): Promise<void> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/products/${id}`, {
            method: 'DELETE',
            headers,
        });
        if (!response.ok) throw new Error('Failed to delete product');
    }

    async createReview(review: Partial<Review>): Promise<Review> {
        if (this.useMockData) {
            const newReview: Review = {
                id: Date.now(),
                productId: review.productId ?? 0,
                reviewer: review.reviewer ?? 'Anonymous',
                rating: review.rating ?? 5,
                comment: review.comment ?? '',
                createdAt: new Date().toISOString(),
            };
            const product = mockProducts.find(p => p.id === review.productId);
            if (product) {
                product.reviews.push(newReview);
            }
            return newReview;
        }
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/reviews`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify(review),
        });
        if (!response.ok) throw new Error('Failed to create review');
        return response.json();
    }

    // ── Cart methods ──

    async getCart(): Promise<Cart> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/cart`, { headers });
        if (!response.ok) throw new Error('Failed to fetch cart');
        return response.json();
    }

    async addToCart(productId: number, quantity: number = 1): Promise<void> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/cart/items`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify({ productId, quantity }),
        });
        if (!response.ok) throw new Error('Failed to add to cart');
    }

    async updateCartItem(itemId: number, quantity: number): Promise<void> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/cart/items/${itemId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify({ quantity }),
        });
        if (!response.ok) throw new Error('Failed to update cart');
    }

    async removeCartItem(itemId: number): Promise<void> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/cart/items/${itemId}`, {
            method: 'DELETE',
            headers,
        });
        if (!response.ok) throw new Error('Failed to remove item');
    }

    async clearCart(): Promise<void> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/cart`, {
            method: 'DELETE',
            headers,
        });
        if (!response.ok) throw new Error('Failed to clear cart');
    }

    // ── Order methods ──

    async checkout(): Promise<Order> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/orders/checkout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...headers },
        });
        if (!response.ok) throw new Error('Failed to checkout');
        return response.json();
    }

    async getOrders(): Promise<Order[]> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/orders`, { headers });
        if (!response.ok) throw new Error('Failed to fetch orders');
        return response.json();
    }

    async getOrder(id: number): Promise<Order> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/orders/${id}`, { headers });
        if (!response.ok) throw new Error('Failed to fetch order');
        return response.json();
    }

    // ── Admin methods ──

    async getDashboard(): Promise<DashboardData> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/admin/dashboard`, { headers });
        if (!response.ok) throw new Error('Failed to fetch dashboard');
        return response.json();
    }

    async getAdminOrders(status?: string): Promise<AdminOrder[]> {
        const headers = await this.authHeaders();
        const url = status ? `${this.baseUrl}/admin/orders?status=${status}` : `${this.baseUrl}/admin/orders`;
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error('Failed to fetch orders');
        return response.json();
    }

    async updateOrderStatus(orderId: number, status: string): Promise<void> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/admin/orders/${orderId}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify({ status }),
        });
        if (!response.ok) throw new Error('Failed to update order status');
    }

    async toggleProductStock(productId: number, inStock: boolean): Promise<void> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/admin/products/${productId}/stock`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify({ inStock }),
        });
        if (!response.ok) throw new Error('Failed to update stock');
    }

    // ── Discount Code methods ──

    async getDiscountCodes(): Promise<DiscountCode[]> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/admin/discounts`, { headers });
        if (!response.ok) throw new Error('Failed to fetch discount codes');
        return response.json();
    }

    async createDiscountCode(discountPercent: number): Promise<DiscountCode> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/admin/discounts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify({ discountPercent }),
        });
        if (!response.ok) throw new Error('Failed to create discount code');
        return response.json();
    }

    async updateDiscountCode(id: number, discountPercent: number): Promise<DiscountCode> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/admin/discounts/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify({ discountPercent }),
        });
        if (!response.ok) throw new Error('Failed to update discount code');
        return response.json();
    }

    async invalidateDiscountCode(id: number): Promise<void> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/admin/discounts/${id}/invalidate`, {
            method: 'PUT',
            headers: await this.authHeaders(),
        });
        if (!response.ok) throw new Error('Failed to invalidate discount code');
    }

    async extendDiscountCode(id: number): Promise<void> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/admin/discounts/${id}/extend`, {
            method: 'PUT',
            headers,
        });
        if (!response.ok) throw new Error('Failed to extend discount code');
    }

    async validateDiscountCode(code: string): Promise<DiscountValidation> {
        const headers = await this.authHeaders();
        const response = await fetch(`${this.baseUrl}/discounts/validate/${encodeURIComponent(code)}`, { headers });
        if (!response.ok) {
            const err = await response.json().catch(() => ({ message: 'Invalid code' }));
            throw new Error(err.message || 'Invalid discount code');
        }
        return response.json();
    }
}

const dataService = new DataService();
export default dataService;
